import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { LanguageSelectorModel } from './model/language-selector.model';
import { LANGUAGES } from './language-selector-data';
import { LanguageSelectorService } from './service/language-selector.service';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';

@Component({
  selector: 'prep-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
})
export class LanguageSelectorComponent
  implements OnInit, AfterViewInit, OnDestroy {
  languages: LanguageSelectorModel[] = LANGUAGES;
  selectedLang: LanguageSelectorModel = this.languages[0];

  @ViewChild(DropDownListComponent) private kendoDropDown?: DropDownListComponent;
  @ViewChild(DropDownListComponent, { read: ElementRef })
  private kendoDropDownEl?: ElementRef<HTMLElement>;
  @ViewChild('langAnnouncement') private langAnnouncement?: ElementRef<HTMLDivElement>;

  private popupOpen = false;
  // Kendo fires (close) synchronously, then valueChange in the same turn,
  // when the user commits via Enter or item-click. The grace flag lets
  // those commits through; closed-popup arrow nav never fires (close), so
  // it can't slip through this gate.
  private commitGracePeriod = false;
  // Set when the popup is being closed because the user cancelled
  // (Esc / Tab away), so onPopupClose suppresses the grace period and
  // any subsequent Kendo-emitted valueChange (from keydown commit OR the
  // blur-commit path on Tab) is rejected by onLanguageChange.
  private cancelClose = false;
  private capturedKeyHandler?: (event: KeyboardEvent) => void;
  private documentKeyHandler?: (event: KeyboardEvent) => void;
  private readonly listboxId = 'langSelect-listbox';

  labels: { [key: string]: { label: string; aria: string } } = {
    en: { label: 'Language', aria: 'Language selector' },
    es: { label: 'Idioma', aria: 'Selector de idioma' },
    it: { label: 'Lingua', aria: 'Selettore della lingua' },
  };

  // Getters to return correct label text
  get currentLabel(): string {
    return this.labels[this.selectedLang.code]?.label || 'Language';
  }

  get currentAriaLabel(): string {
    return this.labels[this.selectedLang.code]?.aria || 'Language selector';
  }

  // Exposes popup-open state to the template so aria-controls is only
  // present while the listbox is actually rendered (WCAG 4.1.2). When the
  // popup is closed the referenced listbox element does not exist, so the
  // host must not advertise aria-controls.
  get isPopupOpen(): boolean {
    return this.popupOpen;
  }

  constructor(private languageSelectorService: LanguageSelectorService) {}

  ngOnInit(): void {
    this.selectedLang = this.languageSelectorService.getCurrentLanguage();
  }

  ngAfterViewInit(): void {
    // Register keydown in capture phase so we run BEFORE Kendo's host
    // listener and can suppress two specific defaults (WCAG 2.1.1, 3.2.1):
    //   - Escape on an OPEN popup: Kendo commits the highlighted option
    //     and translates the page when the user is trying to cancel.
    //   - Tab from an OPEN popup: same problem via keydown commit + a
    //     blur-commit path when focus moves away.
    // Enter, item-click, open-popup arrow navigation, and arrow-on-closed
    // are intentionally left alone — the arrow-on-closed value cycle is
    // caught by the onLanguageChange backstop instead, so Kendo can still
    // initialize its highlight index for in-popup arrow navigation.
    const host = this.kendoDropDownEl?.nativeElement;
    if (host) {
      this.capturedKeyHandler = (event) => this.onCaptureKeyDown(event);
      host.addEventListener('keydown', this.capturedKeyHandler, true);
      // Document-level capture is the highest-priority point in the
      // propagation path (window → document → … → target), so it runs
      // before any host-level listener regardless of how/when Kendo's
      // @HostListener was registered. We scope by target containment so
      // we don't interfere with keydowns elsewhere on the page.
      this.documentKeyHandler = (event) => {
        const target = event.target as Node | null;
        if (target && host.contains(target)) {
          this.onCaptureKeyDown(event);
        }
      };
      document.addEventListener('keydown', this.documentKeyHandler, true);
    }
  }

  ngOnDestroy(): void {
    const host = this.kendoDropDownEl?.nativeElement;
    if (host && this.capturedKeyHandler) {
      host.removeEventListener('keydown', this.capturedKeyHandler, true);
    }
    if (this.documentKeyHandler) {
      document.removeEventListener('keydown', this.documentKeyHandler, true);
    }
  }

  onPopupOpen(): void {
    this.popupOpen = true;
    this.commitGracePeriod = false;
    // Reset stale cancel flag from any prior interaction. If a previous
    // Esc/Tab failed to fire (close) (e.g. popupOpen drifted out of sync
    // with Kendo's internal state, or toggle(false) no-op'd), cancelClose
    // could still be true and would silently reject the user's first
    // Enter commit on the next open. A fresh open MUST start from a
    // clean slate.
    this.cancelClose = false;
    // Tag the freshly-rendered Kendo popup listbox with the id our combobox
    // host references via aria-controls (WCAG 4.1.2). Kendo renders the
    // popup in a body-level portal so we have to wait for it to land.
    setTimeout(() => this.assignListboxId(), 0);
  }

  onPopupClose(): void {
    this.popupOpen = false;
    if (this.cancelClose) {
      // User pressed Esc or tabbed away — do NOT allow Kendo's pending
      // valueChange (keydown commit or blur-commit) to translate the page.
      this.cancelClose = false;
      this.commitGracePeriod = false;
      return;
    }
    // Allow a synchronous Kendo-driven valueChange (Enter / item-click) to
    // commit. Cleared in the next microtask so closed-popup arrow nav,
    // which doesn't go through (close), still hits the suppression gate.
    this.commitGracePeriod = true;
    Promise.resolve().then(() => {
      this.commitGracePeriod = false;
    });
  }

  onLanguageChange(code: string): void {
    // Bug #16 backstop: Kendo's selectByIndex on a closed popup emits
    // valueChange without firing (open)/(close). Reject any change that
    // didn't come from an actually-open (or just-closed) popup so even if
    // the capture-phase keydown intercept is bypassed, the page is not
    // translated by a stray arrow press (WCAG 2.1.1).
    if (!this.popupOpen && !this.commitGracePeriod) {
      // Kendo already mutated its internal _value to the cycled option;
      // push it back to the actual selection so the trigger and the
      // listbox highlight stay correct on the next popup open. The value
      // setter calls setState() but does NOT call emitChange, so this
      // does not loop.
      if (
        this.kendoDropDown &&
        this.kendoDropDown.value !== this.selectedLang.code
      ) {
        this.kendoDropDown.value = this.selectedLang.code;
      }
      return;
    }
    const selected = this.languages.find((lang) => lang.code === code);
    if (!selected || selected.code === this.selectedLang.code) {
      return;
    }
    this.selectedLang = selected;
    this.languageSelectorService.setLanguage(selected);
    this.announceLanguageChange(selected);
  }

  private onCaptureKeyDown(event: KeyboardEvent): void {
    // Bug #16 (WCAG 2.1.1) is handled by the onLanguageChange backstop
    // (popupOpen + commitGracePeriod gate), NOT by intercepting the
    // arrow-on-closed keydown. The earlier intercept used
    // stopImmediatePropagation, which also killed Kendo's _focused
    // initialization for the popup, breaking subsequent arrow-key
    // navigation inside the open popup. Letting Kendo's default run:
    //   - opens the popup (correct)
    //   - cycles its internal value (rejected by the backstop, page does
    //     NOT translate, kendoDropDown.value is reverted)
    //   - initializes _focused so ↑/↓ in the open popup works
    // Bug #15: Esc must close the popup without committing the highlighted
    // option. Kendo's default Esc handler commits the highlight and fires
    // valueChange — capture-phase stopImmediatePropagation kills it before
    // it runs, then we close the popup ourselves. cancelClose tells
    // onPopupClose to skip the commit grace period so any straggler
    // valueChange (e.g. from blur) is rejected.
    if (event.key === 'Escape' && this.popupOpen) {
      this.cancelClose = true;
      // Force-close our state synchronously. Kendo's toggle() defers the
      // (close) event behind a setTimeout, so without this any stray
      // valueChange that fires before the next tick would see popupOpen
      // still true and slip through onLanguageChange's gate.
      this.popupOpen = false;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.kendoDropDown?.toggle(false);
      return;
    }
    // Bug #17 (WCAG 2.1.1): Tab from an OPEN popup must close the popup
    // WITHOUT committing the highlighted option. Kendo runs two commit
    // paths on Tab — its keydown handler AND a commit-on-blur path when
    // focus moves to the next element. stopImmediatePropagation kills the
    // keydown path. The blur-commit is harder: Kendo's toggle() is async
    // (uses setTimeout internally), so (close) fires AFTER blur. By then
    // Kendo has already emitted valueChange. We force popupOpen=false
    // synchronously so onLanguageChange's gate (!popupOpen &&
    // !commitGracePeriod) rejects the blur-driven commit and reverts
    // Kendo's internal value back to selectedLang.code. preventDefault is
    // intentionally omitted so Tab still moves focus to the next element.
    if (event.key === 'Tab' && this.popupOpen) {
      this.cancelClose = true;
      this.popupOpen = false;
      event.stopImmediatePropagation();
      this.kendoDropDown?.toggle(false);
      return;
    }
  }

  private assignListboxId(): void {
    const popups = document.querySelectorAll<HTMLElement>(
      '.k-animation-container [role="listbox"], kendo-popup [role="listbox"]'
    );
    if (!popups.length) {
      return;
    }
    // Use the most recently rendered popup (last in document order).
    const target = popups[popups.length - 1];
    if (target.id !== this.listboxId) {
      target.id = this.listboxId;
    }
  }

  // Imperative update of the aria-live region so the same message is
  // reliably re-announced (clear-then-set pattern avoids SR dedup).
  private announceLanguageChange(selected: LanguageSelectorModel): void {
    const el = this.langAnnouncement?.nativeElement;
    if (!el) {
      return;
    }
    el.textContent = '';
    setTimeout(() => {
      el.textContent = `Page language changed to ${selected.name}`;
    }, 100);
  }
}
